import { deviceHeaders } from './attendance'
import type { WhatsNewEntry } from './whatsNew'

export type UserRole = 'Owner' | 'Admin' | 'Teacher' | 'Parent' | 'Dancer'

export type Session = {
  accountId: string
  email: string
  displayName: string
  role: UserRole
  /** Capability names, e.g. "ManageClasses". Authoritative; never inferred from role. */
  capabilities: string[]

  /**
   * Opened by PIN on a classroom tablet (D-233).
   *
   * Reported by the server rather than worked out here. A kiosk session and a
   * teacher's differ by a single capability, so anything inferring it from
   * `capabilities` would be guessing — and this decides whether the studio app
   * renders at all.
   */
  isKiosk?: boolean

  /**
   * Which modules this host has switched on (D-625).
   *
   * Optional like everything else here, and read with `=== true`, so a session
   * that predates the field leaves every module off rather than guessing.
   */
  modules?: {
    messaging?: boolean
  }

  /**
   * Who this login stands for (D-305).
   *
   * Two flags rather than one role, because they are not alternatives: sixteen
   * people here are staff **and** a parent at once. The header needs both to
   * know whether to offer the switch at all.
   */
  isStaff?: boolean
  /**
   * This login answers support (D-838).
   *
   * ⚠ <b>Read by nothing since every feedback kind started opening a thread</b>:
   * it existed to decide which options she was offered, and everybody is offered
   * all four now. Kept on the wire because a screen for support is the obvious
   * next thing and this is what it will ask; delete it if that never arrives.
   */
  answersSupport?: boolean
  isParent?: boolean
  /** She **is** a dancer here (D-539). A third relationship, not a variant. */
  isDancer?: boolean

  /**
   * She has **days** here — her own classes, or a night she is covering (D-322).
   *
   * Tonya is staff, holds every capability and teaches nothing, so keyed on
   * `isStaff` her rail carried an empty Schedule every day. Answered by the
   * server because it is a question about the database, not about the session.
   */
  teaches?: boolean

  /**
   * She is the instructor **of record** on a class (D-322).
   *
   * Narrower than `teaches`, and the pair is not redundant: covering a night
   * gives her a day here, not a class. Served by one flag, a substitute got a
   * *My classes* that could not contain anything.
   */
  ownsClasses?: boolean

  /**
   * **Which** staff member this login is, when it is one (D-373).
   *
   * Not an authorisation input — every boundary reads the loaded account
   * (D-72, D-73). It exists so a screen can recognise *her own* rows without a
   * second request asking who she is: the booking form defaults to her instead
   * of making her find herself among all staff, and the week grid narrows its
   * booking overlay to her own privates.
   *
   * Absent for a family. **Present for Tonya** — her owner account is created
   * with `UserAccount.ForStaff`, so she has a staff link like anyone else. That
   * is why a screen deciding "is this me?" must ask `teaches` as well: holding
   * a `staffId` says she is on the staff list, not that she has days here
   * (D-322).
   */
  staffId?: string | null
  /**
   * Which dancer this login **is**, when it is one (D-539).
   *
   * The same argument as `staffId` one link over: not an authorisation input —
   * every boundary reads the loaded account (D-72, D-73) — but a screen showing
   * "her own record" has to know which record that is.
   */
  dancerId?: string | null
  /**
   * The date the app is pretending it is, when it is pretending (D-386).
   *
   * Absent on every ordinary host. It rides on the session rather than having
   * its own request because the banner asking `/api/dev/clock` on every screen
   * meant a 404 — and a console error — on every signed-in page wherever the
   * override is off, which is nearly everywhere.
   */
  simulatedDate?: string | null

  /**
   * She has not yet been told what the app can do for her (D-651).
   *
   * `asked`, from the pilot: *"After first opening the app, a modal telling
   * first-time users they can set up notifications and biometrics in account
   * settings."*
   *
   * On the session rather than its own lookup, for the reason `simulatedDate`
   * is: a separate request shows the screen first and throws a dialog over it a
   * beat later. **Always false on a classroom tablet** — a kiosk session is a
   * room rather than a person, with no account settings to be sent to.
   */
  showWelcome?: boolean
  /** Where a family pays her for a private, if she has set one (D-662). */
  paymentLinks?: string[] | null
  /**
   * The what's-new entries she matches and has not dismissed, newest first (D-731).
   * Empty on a kiosk, and while the welcome is still to come.
   */
  whatsNew?: WhatsNewEntry[]
  /**
   * Whether the bell offers *What's new in the app* today: for fourteen studio days
   * after the newest entry she matches, whatever she has dismissed (D-731). False on
   * a kiosk.
   */
  whatsNewLink?: boolean
}

/**
 * A failure the user is meant to read.
 *
 * The API answers in ProblemDetails, so the useful sentence is already written
 * server-side — repeating it here in different words would give the same
 * condition two names.
 */
export class AuthError extends Error {
  // Declared and assigned rather than as constructor parameter properties:
  // the build runs with `erasableSyntaxOnly`, which rejects the shorthand
  // because it emits real code rather than erasing to nothing.
  readonly status: number
  readonly detail?: string

  constructor(message: string, status: number, detail?: string) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    /*
     * The device token rides along on sign-in (D-519).
     *
     * <b>So that a shared tablet does not get a persistent personal session.</b>
     * The session cookie survives a browser restart now, and the scheme selector
     * picks by cookie *presence* — so one sign-in nobody signed out of would
     * leave a wall tablet answering as that account indefinitely, with the kiosk
     * ceiling bypassed, which is D-234's outcome arriving through the other
     * cookie. The server reads this header and withholds persistence.
     *
     * Sent from here rather than only from the attendance calls because sign-in
     * is where the decision is made. It is absent on every ordinary browser, so
     * nothing else changes.
     */
    headers: { 'Content-Type': 'application/json', ...deviceHeaders() },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new AuthError(
      problem?.title ?? 'Something went wrong. Please try again.',
      res.status,
      problem?.detail,
    )
  }

  return res.status === 204 ? (undefined as T) : res.json()
}

export const signIn = (email: string, password: string) =>
  post<Session>('/api/auth/signin', { email, password })

export const acceptInvite = (token: string, password: string) =>
  post<Session>('/api/auth/accept-invite', { token, password })

export const signOut = () => post<void>('/api/auth/signout', {})

/**
 * She has been told what the app can do for her (D-651).
 *
 * The first call wins and every later one is a no-op, so a second tab or a
 * retry cannot re-stamp it — which matters because this records that something
 * happened once rather than a read position that moves.
 */
export const markWelcomeSeen = () => post<{ seenAt: string }>('/api/auth/welcome-seen', {})

/**
 * Asks the studio to send a set-up link to this address (D-155).
 *
 * <b>Always succeeds, and always says the same thing.</b> The server answers
 * identically whether or not the address is on file, because anything else
 * turns the form into a membership oracle — type an address, learn whether that
 * family dances here. The screen has to be written to match, so it never
 * reports "not found" even when it would be true.
 */
/**
 * Asks for a password reset link.
 *
 * Answers identically whatever it finds, for the same reason as the claim form:
 * distinguishing an unknown address from a known one hands over a list of who
 * is registered.
 */
export const forgotPassword = (email: string) =>
  post<{ message: string }>('/api/auth/forgot-password', { email })

/** Sets a new password from a reset link, and signs in. */
export const resetPassword = (token: string, password: string) =>
  post<Session>('/api/auth/reset-password', { token, password })

export const claimAccount = (email: string) =>
  post<{ message: string; open: boolean }>('/api/auth/claim-account', { email })

/**
 * Whether a family may set up her own login (D-614).
 *
 * **Closed during the pilot**, so the only accounts are the ones the studio
 * sent a link for. The server decides and enforces it; this only asks, so that
 * the page does not offer a door that is shut.
 *
 * **Throws rather than answering "shut" when it cannot tell**, which is the
 * difference between hiding a link and making a claim. Every database-backed
 * endpoint refuses for about 75 seconds after a deploy while the SPA itself
 * loads perfectly (D-33), so a failure swallowed into `false` would tell real
 * families that self-service is closed, on a studio where it is open, and would
 * stop the query ever retrying (D-209).
 */
export async function claimAccountOpen(): Promise<boolean> {
  const res = await fetch('/api/auth/claim-account/available')
  if (!res.ok) throw new Error(`Could not ask whether that is open (${res.status})`)

  return (await res.json()).open === true
}

/**
 * The current session, or null.
 *
 * A 401 is the expected answer for a signed-out visitor, not an error — turning
 * it into a thrown exception would make every first page load look like a
 * failure and trigger retries against an endpoint that is answering correctly.
 */
export async function fetchSession(): Promise<Session | null> {
  const res = await fetch('/api/auth/me')
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`Session check failed (${res.status})`)
  return res.json()
}

/** Mirrors the server's rule so the form can say so before a round trip (D-74). */
export const MIN_PASSWORD_LENGTH = 12

/** PasswordPolicy.MaximumLength. Long passphrases are encouraged; 256 is the ceiling. */
export const MAX_PASSWORD_LENGTH = 256

export type PasswordProblem = {
  /** Which box to point at, so the message appears where the fix is. */
  field: 'password' | 'confirm'
  message: string
}

/**
 * Why this pair of passwords cannot be submitted, or null.
 *
 * <b>Every branch returns a sentence.</b> Both password screens previously
 * guarded submit with `if (tooShort || mismatch || !password) return`, which
 * made the button do nothing at all and say nothing about why — reported as the
 * form being "locked up" (D-186). A validation rule that can silently reject is
 * worse than no validation, because the person has no next move.
 *
 * Pure and exported so it is tested directly: the failure mode here is a
 * condition nobody can see, which is exactly what a unit test can.
 */
export function passwordProblem(password: string, confirm: string): PasswordProblem | null {
  if (password.length === 0)
    return { field: 'password', message: 'Choose a password first.' }

  // Counts out loud. "At least 12" plus a box you cannot read the length of
  // leaves someone guessing how close they got.
  if (password.length < MIN_PASSWORD_LENGTH)
    return {
      field: 'password',
      message: `That is ${password.length} character${password.length === 1 ? '' : 's'}. It needs at least ${MIN_PASSWORD_LENGTH}.`,
    }

  if (password.length > MAX_PASSWORD_LENGTH)
    return {
      field: 'password',
      message: `That is longer than ${MAX_PASSWORD_LENGTH} characters.`,
    }

  // Was not checked at all: an empty confirmation made `mismatch` false, so the
  // form submitted a password nobody had typed twice.
  if (confirm.length === 0)
    return { field: 'confirm', message: 'Type the password a second time to confirm it.' }

  if (password !== confirm)
    return { field: 'confirm', message: 'The two passwords do not match.' }

  return null
}
